'use client';

import { type FC } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { Select, SelectContent, SelectItem } from '@packages/shared/src/ui/Select/Select';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { unmask } from '@packages/gql/src/betterMasking';
import type { DisplaySettingsDesktopFragmentFragmentDoc } from '@packages/gql/generated/shopping/DisplaySettingsDesktopFragmentFragmentDoc';
import { PRODUCT_LIST_ID } from '../../ids';

import { useDisplayMode } from '../../hooks/useDisplayMode';
import type { DesktopDisplayMode } from '../../types';
import { SortingOptionsSelect } from '../SortingOptionsSelect/SortingOptionsSelect';

/* GraphQL */ `
  fragment DisplaySettingsDesktopFragment on SearchProductResult {
    ...UseDisplayModeFragment
    ...UsePagingFragment
    ...SortingOptionsSelectFragment
    availableSortingOrders {
      key
      displayName
    }
  }
`;

export type DisplaySettingsDesktopProps = {
  maskedData: FragmentType<typeof DisplaySettingsDesktopFragmentFragmentDoc>;
};

const messages = defineMessages({
  displayModeLabel: {
    id: 'search.productList.displaySettings.displayMode.label',
    defaultMessage: 'Ansicht wechseln',
  },
  displayModeCards: {
    id: 'search.productList.displaySettings.displayModes.cards',
    defaultMessage: 'Kleine Bilder',
  },
  displayModeLargeCards: {
    id: 'search.productList.displaySettings.displayModes.largeCards',
    defaultMessage: 'Große Bilder',
  },
  displayModeMoreInformation: {
    id: 'search.productList.displaySettings.displayModes.moreInformation',
    defaultMessage: 'Mehr Informationen',
  },
  pagingModeInfinite: {
    id: 'search.productList.displaySettings.pagingMode.infinite',
    defaultMessage: 'Alle anzeigen',
  },
  pagingModeLimited: {
    id: 'search.productList.displaySettings.pagingMode.limited',
    defaultMessage: '72 Artikel pro Seite',
  },
  productListDisplayOptions: {
    id: 'search.productList.displayOptions',
    defaultMessage: 'Anzeigeoptionen',
  },
});

/**
 * Component to allow the user to change the display settings for the product list
 * */
export const DisplaySettingsDesktop: FC<DisplaySettingsDesktopProps> = ({ maskedData }) => {
  const data = unmask<typeof DisplaySettingsDesktopFragmentFragmentDoc>(maskedData);
  const { formatMessage } = useIntl();
  const { displayMode, pushDisplayModeChange } = useDisplayMode(data);
  const displayModes = [
    { value: 'cards', label: formatMessage(messages.displayModeCards) },
    { value: 'largeCards', label: formatMessage(messages.displayModeLargeCards) },
    { value: 'moreInformation', label: formatMessage(messages.displayModeMoreInformation) },
  ];
  const displayModelabelId = formatMessage(messages.displayModeLabel)
    .toLowerCase()
    .replace(/\s+/g, '-');
  return (
    <nav
      aria-label={formatMessage(messages.productListDisplayOptions)}
      aria-controls={PRODUCT_LIST_ID}
      className="grid grid-cols-4 items-center gap-2"
    >
      <Select
        value={displayMode}
        onValueChange={(event: DesktopDisplayMode) => {
          pushDisplayModeChange(event);
        }}
        id={displayModelabelId}
        label={formatMessage(messages.displayModeLabel)}
      >
        <SelectContent sideOffset={-8} className="z-modal">
          {displayModes.map(({ value, label }) => (
            <SelectItem key={value} value={value}>
              {label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <SortingOptionsSelect maskedData={data} />
    </nav>
  );
};
